/**
 * Kanban board view of project tasks, grouped by status into 5 columns.
 * Cards are read-only — status change happens via inline edit in the table
 * or the drawer. No drag-and-drop in v1.
 */
import type { ProjectTask } from "@/hooks/use-tasks";
import { formatRelativeDate, PhaseChip, PriorityChip, TaskAvatar } from "./task-chips";

const COLUMNS: Array<{ id: string; label: string; dotCls: string; statuses: string[] }> = [
  { id: "todo", label: "Todo", dotCls: "var(--slate)", statuses: ["not_started"] },
  {
    id: "progress",
    label: "In Progress",
    dotCls: "var(--amber)",
    statuses: ["in_progress"],
  },
  { id: "review", label: "In Review", dotCls: "var(--violet)", statuses: ["in_review"] },
  { id: "blocked", label: "Blocked", dotCls: "var(--rose)", statuses: ["blocked"] },
  { id: "done", label: "Done", dotCls: "var(--accent)", statuses: ["completed"] },
];

function tasksForColumn(tasks: ProjectTask[], statuses: string[]): ProjectTask[] {
  return tasks.filter((t) => statuses.includes(t.status ?? "not_started"));
}

export function TasksBoard({
  tasks,
  assigneeLabels,
  onOpenTask,
}: {
  tasks: ProjectTask[];
  assigneeLabels: Map<string, string>;
  onOpenTask: (task: ProjectTask) => void;
}) {
  return (
    <div className="board">
      {COLUMNS.map((col) => {
        const rows = tasksForColumn(tasks, col.statuses);
        return (
          <div key={col.id} className="col">
            <div className="col-head">
              <span className="cdot" style={{ background: col.dotCls }} />
              <span className="clbl">{col.label}</span>
              <span className="ccount">{rows.length}</span>
            </div>
            <div className="col-body">
              {rows.map((t) => {
                const due = formatRelativeDate(t.dueDate);
                const tradeName = t.assignedTradeId
                  ? (assigneeLabels.get(t.assignedTradeId) ?? null)
                  : null;
                const contactName = t.assignedToContactId
                  ? (assigneeLabels.get(t.assignedToContactId) ?? null)
                  : null;
                const assigneeLabel =
                  tradeName && contactName
                    ? `${tradeName} · ${contactName}`
                    : (tradeName ?? contactName ?? null);
                const avatarKey = t.assignedToContactId ?? t.assignedTradeId ?? "";
                return (
                  <button type="button" key={t.id} className="card" onClick={() => onOpenTask(t)}>
                    <div className="card-top">
                      <span className="tid">{t.id.slice(0, 6).toUpperCase()}</span>
                      <PhaseChip value={t.phase} />
                    </div>
                    <div className="card-title">{t.title ?? "—"}</div>
                    <div className="card-meta">
                      <PriorityChip value={t.priority} />
                      <span className="spacer" />
                      {due ? <span className="mono">{due.rel}</span> : null}
                      {assigneeLabel ? (
                        <TaskAvatar name={assigneeLabel} keyHint={avatarKey} />
                      ) : null}
                    </div>
                  </button>
                );
              })}
              {rows.length === 0 ? (
                <div className="text-[var(--text-4)] text-[11px] text-center py-3">No tasks</div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
